/**
 * Paid-attribution capture.
 *
 * Runs once at app boot via captureClickIds(). Persists click IDs, UTMs, and
 * landing context to localStorage for 90 days (matches Meta CAPI lookback).
 * Last-touch-wins ONLY on a fresh click ID — direct/organic visits never
 * clobber paid attribution.
 *
 * getAttribution() re-reads _fbp/_fbc cookies on every call so a late Pixel
 * write is reflected on the next POST.
 */

export type Attribution = {
  gclid?: string | null;
  fbclid?: string | null;
  gbraid?: string | null;
  wbraid?: string | null;
  msclkid?: string | null;

  fbp?: string | null;
  fbc?: string | null;

  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;

  landing_url?: string | null;
  landing_referrer?: string | null;
  captured_at?: string | null;
};

const STORAGE_KEY = "src_attribution";
const TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

const CLICK_ID_KEYS = ["gclid", "fbclid", "gbraid", "wbraid", "msclkid"] as const;
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

type StoredAttribution = Attribution & { _ts?: number };

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  try {
    const m = document.cookie.match(
      new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"),
    );
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

function getApexDomain(): string {
  if (typeof window === "undefined") return "";
  const host = window.location.hostname;
  // Strip leading subdomain, keep last 2 labels (best-effort; fine for *.com).
  const parts = host.split(".");
  if (parts.length <= 2) return host;
  return parts.slice(-2).join(".");
}

function setCookie(name: string, value: string, days: number): void {
  if (typeof document === "undefined") return;
  try {
    const exp = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    const domain = getApexDomain();
    const domainAttr = domain && !/^\d+\.\d+\.\d+\.\d+$/.test(domain) ? `; domain=.${domain}` : "";
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${exp}; path=/${domainAttr}; SameSite=Lax`;
  } catch {
    // ignore
  }
}

function readStored(): StoredAttribution | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAttribution;
    if (!parsed || typeof parsed !== "object") return null;
    if (parsed._ts && Date.now() - parsed._ts > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(blob: StoredAttribution): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...blob, _ts: Date.now() }));
  } catch {
    // ignore
  }
}

function readUrlClickIds(): { hasNewClickId: boolean; values: Partial<Attribution> } {
  const out: Partial<Attribution> = {};
  let hasNewClickId = false;
  if (typeof window === "undefined") return { hasNewClickId, values: out };
  try {
    const params = new URLSearchParams(window.location.search);
    for (const k of CLICK_ID_KEYS) {
      const v = params.get(k);
      if (v) {
        out[k] = v;
        hasNewClickId = true;
      }
    }
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (v) out[k] = v;
    }
  } catch {
    // ignore
  }
  return { hasNewClickId, values: out };
}

/**
 * Capture click IDs + UTMs at first touch, synthesize _fbc if needed, and
 * persist for 90 days. Call once at app boot, before React renders.
 */
export function captureClickIds(): void {
  if (typeof window === "undefined") return;

  const { hasNewClickId, values: fromUrl } = readUrlClickIds();

  // Synthesize _fbc from fbclid if Pixel hasn't written one yet.
  if (fromUrl.fbclid && !readCookie("_fbc")) {
    const synth = `fb.1.${Date.now()}.${fromUrl.fbclid}`;
    setCookie("_fbc", synth, 90);
  }

  const existing = readStored();

  let next: StoredAttribution;
  if (hasNewClickId || !existing) {
    // Fresh paid click (or no prior attribution) — overwrite.
    next = {
      ...fromUrl,
      landing_url: window.location.href,
      landing_referrer: typeof document !== "undefined" ? document.referrer : "",
      captured_at: new Date().toISOString(),
    };
  } else {
    // Preserve first-touch; do not let direct/organic overwrite paid.
    next = { ...existing };
  }

  // Always refresh Meta cookies on every boot.
  next.fbp = readCookie("_fbp");
  next.fbc = readCookie("_fbc");

  writeStored(next);
}

/**
 * Returns the current attribution blob with always-fresh _fbp/_fbc cookies.
 * Safe to call before captureClickIds() — returns whatever cookies + storage
 * hold at the moment, with empty strings coerced to null.
 */
export function getAttribution(): Attribution {
  const stored = readStored() ?? {};
  const out: Attribution = { ...stored };
  delete (out as StoredAttribution)._ts;

  // Always refresh cookies — pixel may have written after first capture.
  const fbp = readCookie("_fbp");
  const fbc = readCookie("_fbc");
  if (fbp) out.fbp = fbp;
  if (fbc) out.fbc = fbc;

  // Coerce empty strings to null defensively.
  for (const k of Object.keys(out) as (keyof Attribution)[]) {
    if (out[k] === "") out[k] = null;
  }
  return out;
}
